// Which door, if any, the shared F/G interact prompt should offer.
//
// Split out of doors.js as pure arithmetic over the door records so it can be
// unit-tested in plain Node: package.json has no "type":"module", so a .js
// under src/ loads as CommonJS and breaks, and doors.js itself pulls in
// Babylon. Same reason src/world/query.mjs and infected-sim.mjs are .mjs.
//
// Two things matter here and neither is testable in a browser cheaply:
//   • cost — this runs once per frame from main.js's interactTarget(), and
//     walking all 839 doors on the map to find one 2.4 m away is absurd, so it
//     reads the same 16 m spatial hash the collider uses;
//   • intent — a doorway BEHIND you used to be a candidate, so a door at your
//     back could win the prompt over the crate you are looking at.

export const CELL = 16;          // must match the hash Doors builds
export const REACH = 2.4;        // interaction distance from the door centre
// Standing in the opening the bearing to the door centre is noise (you are
// basically on top of it), so inside this radius the facing test is dropped.
export const FACE_ANY = 0.8;
// cos of the half-angle you must be looking within: ~75°, i.e. the door only
// has to be somewhere in front of you, not centred in the crosshair.
export const FACE_DOT = 0.25;

export const cellKey = (x, z) => `${Math.floor(x / CELL)}_${Math.floor(z / CELL)}`;

// hash: Map(cellKey -> door[]) exactly as Doors builds it; each door needs
// only {cx, cz}. pos: {x, z}. fwd: {x, z} camera forward (any length) or null
// to skip the facing test. Returns the door object or null.
export function pickNearest(hash, pos, fwd = null, reach = REACH) {
  const kx = Math.floor(pos.x / CELL), kz = Math.floor(pos.z / CELL);
  // reach is well under one cell, so the player's cell plus its 8 neighbours
  // always contains every door that could be in range
  let fx = 0, fz = 0;
  if (fwd) {
    const len = Math.hypot(fwd.x, fwd.z);
    if (len > 1e-6) { fx = fwd.x / len; fz = fwd.z / len; }
  }
  let best = null, bd = reach * reach;
  for (let dz = -1; dz <= 1; dz++) {
    for (let dx = -1; dx <= 1; dx++) {
      const list = hash.get(`${kx + dx}_${kz + dz}`);
      if (!list) continue;
      for (const d of list) {
        const ox = d.cx - pos.x, oz = d.cz - pos.z;
        const dist2 = ox * ox + oz * oz;
        if (dist2 >= bd) continue;
        if ((fx || fz) && dist2 > FACE_ANY * FACE_ANY) {
          const inv = 1 / Math.sqrt(dist2);
          if (ox * inv * fx + oz * inv * fz < FACE_DOT) continue;
        }
        bd = dist2; best = d;
      }
    }
  }
  return best;
}
